/* Pocket Factory · art for the whole front: the art step of the one design pipeline (assets/pocket-design.js), and a
   finished design the shopper uploads ("Upload it for the whole front"). Markup: snippets/pocket-ai-design.liquid.
   Contract (request, errors, order line properties): docs/part3/contracts.md.

   There is no prompt of its own any more (Oct 22, docs/editor-brainstorm-1022.md): the hero's "Anything specific?" box
   is the one box. PocketDesign reads their words and calls make(words) here, in the background: the Worker makes
   full-card art (POST /v1/art/generate: 3 a day per network and per tab) while the page stays usable, and the design is
   put on the card when it lands (apply). This tab keeps up to 3 designs (sessionStorage pf_ai_designs), numbered as
   they were made, to switch between in the Art chip. "On your card" draws a design full-bleed under the logo or name
   (PocketCards setBackgroundArt) and writes the order line properties to the product form, whose own sync (pocket.js)
   then makes _Artwork source "ai-generated". A finished design they upload is saved the same way (as artwork, below) and
   goes on the card as it is ("customer-upload", their logo and name off: "Artwork Only"); its row shows the moment the
   file is picked, so the page never moves when it is saved, and pocket:card-config is said when the upload starts and
   ends (Buy waits on state().saving). "Show my logo or name on it" turns the logo and name over any art on or off
   (_Name over art yes/no), shown when there is a name or logo to show. Changes of their own design are said once, by
   the page's one announcer ([data-pfai-live]); art from their words is said by PocketDesign. make() works only when
   /v1/capabilities says aiArt; the upload is always offered. Worker text is only ever set as text, never as HTML. A
   change of art on a saved card (?card=) is saved to it at once (pocket-card-profile.js settle()); the tab remembers
   which card its art went on, so that card read back older by a quick reload keeps it. */
(function(root,factory){
  const api=factory();
  if(typeof module==='object'&&module.exports)module.exports=api;
  else if(!root.PocketAiDesign){root.PocketAiDesign=api;api.page=api.init(root.document);}
})(typeof window!=='undefined'?window:globalThis,function(){
  'use strict';
  // SLUG: a saved card's address (?card=), as assets/pocket-card-profile.js reads it.
  const ASSET=/^asset_[a-f0-9]{32}$/,SLUG=/^[a-z0-9][a-z0-9-]{2,63}$/,KEY='pf_ai_designs',MAX=3,MIN_TEXT=3,MAX_TEXT=300,GENERATE_MS=90000,UPLOAD_MS=60000;
  // _Artwork description for a design made from a mockup alone (contract wording).
  const FROM_MOCKUP="From the customer's mockup";
  const IMAGE_TYPES=['image/png','image/jpeg','image/webp','image/gif'];
  const T={
    network:'We couldn’t reach the design service.',timeout:'Your art is taking longer than it should.',
    failed:'We couldn’t make your art right now.',unusable:'We couldn’t show your art.',
    off:'AI designs aren’t available right now.',tooShort:'Describe the look in a few more words.',
    applied:'Your AI design is on your card.',removed:'AI design removed.',
    // A finished design of their own, and the logo and name over any art.
    onAi:'AI design on your card',onOwn:'Your design on the front',whatAi:' your AI design',whatOwn:' your design',
    ownSaving:'Uploading your design…',ownApplied:'Your design is on the front.',
    ownRemoved:'Your design is off the card.',ownAsk:'Upload the design for this card.',ownWithout:'Your card is ready without that design.',
    // After Remove, what the front shows now.
    shows:{both:'The front shows your logo and name.',logo:'The front shows your logo.',name:'The front shows your business name.'},
    ownType:'Use a PNG, JPG, WebP, GIF, HEIC or PDF for your design.',ownFailed:'We couldn’t upload your design. Try again, or use a PNG or JPG version of it.',
    tooSmall:'This image is too small. Choose a larger version.',
    ownTrimmed:'The edges are trimmed to the card’s shape.',ownSoft:'This may print soft. A larger file prints sharper.',
    ownTurned:{Horizontal:'The card is now horizontal, to match your design.',Vertical:'The card is now vertical, to match your design.'},
    ownShape:{Horizontal:'Your design is horizontal.',Vertical:'Your design is vertical.'},madeFor:{Horizontal:'Made for a horizontal card.',Vertical:'Made for a vertical card.'},
    topOn:'Your logo or name is on the design again.',topOff:'Your logo and name are off. The front shows your design alone.'
  };
  // The card's shape (CR80, 85.6 × 54 mm), and the short side a full-front design prints sharp from (54 mm at about
  // 260 dpi; 300 dpi is 638 px).
  const CARD_RATIO=85.6/54,SHARP_SHORT=560;
  /** The description as the Worker reads it: control characters and runs of spaces become one space, trimmed. */
  function clean(value){return String(value??'').normalize('NFC').replace(/[\u0000-\u001f\u007f]/g,' ').replace(/\s+/g,' ').trim();}
  const chars=text=>[...text].length;
  /**
   * A design kept for this visit, or null: only an asset id, its number ("Design 2", kept for good once given), the
   * size, orientation and words; its address is rebuilt.
   */
  function design(input){
    if(!input||!ASSET.test(String(input.assetId||'')))return null;
    const orientation=input.orientation==='Vertical'?'Vertical':'Horizontal',size=(n,fallback)=>Number.isInteger(n)&&n>=8&&n<=4096?n:fallback;
    const own=input.own===true,n=Number.isInteger(input.n)&&input.n>0&&input.n<1e4?input.n:0;
    return {assetId:input.assetId,orientation,width:size(input.width,orientation==='Vertical'?713:1086),height:size(input.height,orientation==='Vertical'?1086:713),
      description:own?'':typeof input.description==='string'?[...clean(input.description)].slice(0,MAX_TEXT).join(''):'',fromMockup:!own&&input.fromMockup===true,typed:!own&&input.typed===true,...(n?{n}:{}),...(own?{own}:{})};
  }
  /** _Artwork description: the shopper's description, or the contract's words for a design made from their mockup alone. */
  function artDescription(d){return d.description&&(d.typed||!d.fromMockup)?d.description:d.fromMockup?FROM_MOCKUP:d.description;}
  /**
   * The order line properties for art on the card ({} clears them): an AI design ("ai-generated", with its description)
   * or their own finished design ("customer-upload"). _Name over art says whether their logo or name is drawn over it.
   */
  function properties(d,{top=true}={}){
    if(!d)return {};
    const p={'_Background art':d.assetId,'_Background art source':d.own?'customer-upload':'ai-generated','_Background art fit':'fill','_Name over art':top?'yes':'no'};
    if(!d.own)p['_Artwork description']=artDescription(d);
    return p;
  }
  const FIELDS={'_Background art':'[data-order-bg-art]','_Background art source':'[data-order-bg-art-source]','_Background art fit':'[data-order-bg-art-fit]','_Name over art':'[data-order-name-over-art]','_Artwork description':'[data-order-art-description]'};

  function init(doc,options={}){
    if(!doc?.defaultView)return null;
    const win=doc.defaultView,places0=doc.querySelector('[data-pfai]');
    const form=doc.querySelector('[data-pocket-product] form.product-form');
    if(!places0||!form||form.dataset.pfaiReady)return null;
    form.dataset.pfaiReady='true';
    // Rendered with step 01, inside the product form: moved out so its words never reach the form's own listeners.
    const live=doc.querySelector('[data-pfai-live]');
    if(live&&live.parentElement!==doc.body)doc.body.append(live);
    let storage=null;try{storage=options.storage!==undefined?options.storage:win.sessionStorage;}catch(_){}
    const core=()=>win.PocketCore,base=()=>String(core()?.workerBase?.()||'').replace(/\/+$/,''),urlOf=id=>base()+'/v1/assets/'+id+'/content';
    const cards=()=>win.PocketPersonalization?.cards;
    let enabled=false,designs=[],remaining=null,resting='',restingText='',applied=null,dismissed='',making=null,ownJob=null,asking=false;
    // The saved card (?card= slug, assets/pocket-card-profile.js) the art went on in this tab: its older revision,
    // read back by a reload before its save landed, never takes the art off.
    let artCard='';
    // The Worker's switch (GET /v1/capabilities, fetched once per page by pocket-core.js): nothing shows without aiArt.
    const ask=options.capabilities||(()=>core()?.capabilities?.()||Promise.resolve({}));
    const ready=Promise.resolve().then(()=>ask()).then(caps=>caps?.aiArt===true,()=>false).then(on=>{enabled=on;render();return on;});

    /* ---------- This visit's designs (sessionStorage pf_ai_designs) ---------- */
    // The art on the card belongs to one design: the one assets/pocket.js names in this tab (sessionStorage
    // pf.designKey.v1, with the keys it had before). Another design never starts with it.
    function designOwner(){
      try{const s=JSON.parse(win.sessionStorage?.getItem('pf.designKey.v1')||'null');if(s&&typeof s.key==='string')return {key:s.key,aliases:Array.isArray(s.aliases)?s.aliases.map(String):[]};}catch(_){}
      return null;
    }
    // The Worker counts designs per UTC day, so a count kept past midnight is dropped.
    const today=()=>new Date().toISOString().slice(0,10);
    function persist(){
      try{storage?.setItem(KEY,JSON.stringify({v:1,day:today(),owner:designOwner()?.key||'',designs,remaining,resting,restingText,applied,dismissed,card:applied?artCard:''}));}catch(_){}
    }
    // true: the tab's state is another design's (a new design begun before this script started).
    function restore(){
      let saved=null;try{saved=JSON.parse(storage?.getItem(KEY)||'null');}catch(_){}
      if(!saved||saved.v!==1)return false;
      designs=(Array.isArray(saved.designs)?saved.designs:[]).map(design).filter(Boolean).slice(0,MAX);
      // Kept before designs had numbers: numbered as they were made (the oldest is 1).
      if(designs.some(d=>!d.n))designs.forEach((d,i)=>{d.n=designs.length-i;});
      if(saved.day===today()){
        remaining=Number.isInteger(saved.remaining)&&saved.remaining>=0&&saved.remaining<=MAX?saved.remaining:null;
        resting=typeof saved.resting==='string'?saved.resting.slice(0,40):'';restingText=typeof saved.restingText==='string'?saved.restingText.slice(0,400):'';
      }
      // Only this design's art comes back on its card; their designs stay in the list either way.
      const now=designOwner(),mine=!!saved.owner&&!!now&&(saved.owner===now.key||now.aliases.includes(saved.owner));
      if(!mine)return true;
      dismissed=ASSET.test(saved.dismissed||'')?saved.dismissed:'';
      const kept=design(saved.applied);if(kept)applied={...kept,source:['profile','upload'].includes(saved.applied.source)?saved.applied.source:'generated'};
      artCard=kept&&SLUG.test(saved.card||'')?saved.card:'';
      return false;
    }

    /* ---------- Helpers ---------- */
    // Drawn and not hidden (a browser has client rects for it; without layout, no hidden or display:none ancestor).
    function shown(node){
      if(!node||node.disabled||!node.isConnected)return false;
      if(node.getClientRects?.().length)return true;
      for(let n=node;n&&n.nodeType===1;n=n.parentElement){if(n.hidden)return false;const style=win.getComputedStyle?.(n);if(style&&(style.display==='none'||style.visibility==='hidden'))return false;}
      return true;
    }
    const reduced=()=>!!win.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    function say(message){if(live)live.textContent=message;}
    // A name or a logo to show over the art: their name (or one typed for the card) or a logo, an upload included. A
    // design uploaded on a fresh page has neither, so "Show my logo or name on it" would change nothing and is not shown.
    function identityShown(){
      const raw=win.PocketPersonalization?.store?.getState?.().brand;if(!raw)return false;
      const config={...cards()?.getConfig?.(),logoMode:'Logo Mark + Wordmark'},assets=win.PocketAssets;
      try{return win.PocketIdentity.selection(assets?.decorate?.(raw,config)||assets?.classify?.(raw)||raw,config).layers.length>0;}catch(_){return !!raw.businessName;}
    }
    // A picture of a chosen file for the art row (PNG, JPG, WebP or GIF as chosen; anything else once made ready).
    function picture(file){try{return IMAGE_TYPES.includes(String(file?.type||'').toLowerCase())&&win.URL.createObjectURL?.(file)||'';}catch(_){return '';}}
    function drop(url){if(url?.startsWith?.('blob:'))try{win.URL.revokeObjectURL(url);}catch(_){}}
    function orientation(){const o=cards()?.getConfig?.().orientation||form.querySelector('[data-card-field="orientation"]:checked')?.value||doc.querySelector('[data-card-field="orientation"]:checked')?.value;return o==='Vertical'?'Vertical':'Horizontal';}
    const places=()=>[...doc.querySelectorAll('[data-pfai]')];
    const placeOf=node=>node?.closest?.('[data-pfai]')||null;
    // The saved card this page shows ('' before there is one).
    const cardSlug=()=>{const s=win.PocketCardProfile?.page?.state?.slug;return typeof s==='string'?s:'';};
    // Their saved card is saved with the art as it is now, like any change of theirs (art is no form field, so nothing
    // else saves it). After this task: the card reads the art from state(), there once init has returned.
    function saveCard(){win.setTimeout(()=>{try{Promise.resolve(win.PocketCardProfile?.page?.settle?.()).catch(()=>{});}catch(_){}},0);}
    // Their logo or name back over the art on a front that showed it alone, when they have one: true when the front
    // shows them. With neither, the front keeps "Artwork only" (its "Your design" space), never a blank card.
    function identityBack(){
      const renderer=cards();if(!renderer?.artOnly?.())return true;
      if(identityShown())renderer.frontWithIdentity?.();
      return !renderer.artOnly();
    }
    // What the front shows without art, judged as question 1 judges it: their logo, their name, both ('' for neither).
    function frontShows(){
      const layers=cards()?.getIdentity?.().layers||[],logo=layers.some(l=>l.kind==='image'&&l.role!=='wordmark'),named=layers.some(l=>l.role==='wordmark'||l.role==='combined');
      return logo&&named?T.shows.both:logo?T.shows.logo:named?T.shows.name:'';
    }

    /* ---------- The order line and the preview ---------- */
    // Their logo or name over the art, unless the front is "Artwork Only".
    const top=()=>!cards()?.artOnly?.();
    function write(values){
      for(const [name,selector] of Object.entries(FIELDS)){
        const input=form.querySelector(selector);if(!input)continue;
        const value=values[name]||'';input.value=value;input.disabled=!value;
      }
    }
    // The shared renderer draws the art on every front preview and says pocket:card-config, which runs pocket.js's own
    // property sync (_Artwork source reads _Background art source).
    function paint(){
      const art=applied?{url:urlOf(applied.assetId),fit:'fill'}:null,renderer=cards();
      // The mockup note's "A design you upload for the whole front prints as it is." (snippets/pocket-gallery.liquid):
      // only while their own design is on the card.
      for(const note of doc.querySelectorAll('[data-pf-upload-note]'))note.hidden=!applied?.own;
      if(typeof renderer?.setBackgroundArt==='function')renderer.setBackgroundArt(art);
      else doc.dispatchEvent(new win.CustomEvent('pocket:card-config'));
    }
    // card: the saved card it goes on (this page's, unless said).
    function apply(d,{source='generated',announce=true,card=cardSlug()}={}){
      const next=design(d);if(!next)return false;
      // An AI design chosen over their own uploaded design replaces it: that upload stays in the order's record, as removed.
      if(applied?.own&&!next.own)win.PocketAssets?.discard?.('background_art');
      applied={...next,source};artCard=card;if(dismissed===next.assetId)dismissed='';
      // A note for their own design (or the ask for one) is not about this one.
      if(!next.own||asking)ownStatus('');
      write(properties(applied,{top:top()}));paint();persist();render();
      if(announce)say(T.applied);
      return true;
    }
    // dismiss: they took it off themselves, so their saved card's copy of it doesn't come back on this design.
    function remove({announce=true,dismiss=true}={}){
      if(!applied)return;
      const own=!!applied.own;
      // Their own design taken off: kept in the order's record of uploads, as removed.
      if(own&&dismiss)win.PocketAssets?.discard?.('background_art');
      dismissed=dismiss?applied.assetId:'';applied=null;artCard='';
      write({});paint();persist();
      // Art taken off a front that showed it alone: their logo or name comes back, so the card is never left blank.
      // With neither, "Artwork only" stays and asks for the design.
      const shows=identityBack();if(!shows)ownStatus(T.ownAsk,{ask:true});
      render();
      // This change's one voice: what came off and what the front shows now. With nothing to show, the ask is the
      // artwork status's to say (pocket.js), and the row shows it.
      if(announce)say([own?T.ownRemoved:T.removed,shows?frontShows():''].filter(Boolean).join(' '));
    }

    /* ---------- Rendering ---------- */
    function render(){renderPlaces();}
    // The art row in every place: the design on the card, or their own design while it is saved (its picture from the
    // file, "Uploading your design…", its actions off), so the row is in place before the upload ends: then only its
    // words change. "Change" goes to the one box their words are in (pocket-design.js: pocket:design-words).
    function renderPlaces(){
      const job=ownJob,on=!!applied||!!job,o=orientation(),own=!!job||!!applied?.own,shape=job?job.shape:applied?.orientation,named=on&&identityShown();
      for(const place of places()){
        // Always there: a finished design can be uploaded with AI off too.
        place.hidden=false;
        for(const node of place.querySelectorAll('[data-pfai-own-row]'))node.hidden=on;
        const row=place.querySelector('[data-pfai-on]');if(!row)continue;
        row.hidden=!on;
        if(!on)continue;
        // A vertical design shows as a narrow picture, never cropped to a sliver.
        if(shape)row.dataset.orientation=shape;else delete row.dataset.orientation;
        const thumb=row.querySelector('[data-pfai-on-thumb]'),url=job?job.preview:urlOf(applied.assetId);
        if(thumb&&thumb.getAttribute('src')!==url){if(url)thumb.src=url;else thumb.removeAttribute('src');}
        const label=row.querySelector('[data-pfai-on-label]');if(label)label.textContent=job?T.ownSaving:own?T.onOwn:T.onAi;
        const change=row.querySelector('[data-pfai-change]');if(change)change.hidden=own||!doc.querySelector('[data-pfc-idea]');
        const replace=row.querySelector('[data-pfai-own-replace]');if(replace){replace.hidden=!own;replace.disabled=!!job;}
        const remove=row.querySelector('[data-pfai-remove]');if(remove)remove.disabled=!!job;
        const what=row.querySelector('[data-pfai-remove-what]');if(what)what.textContent=own?T.whatOwn:T.whatAi;
        const box=row.querySelector('[data-pfai-top]');if(box){box.checked=top();box.disabled=!!job;const wrap=box.closest('.pfai-top');if(wrap)wrap.hidden=!named;}
        const note=row.querySelector('[data-pfai-on-note]');if(note){note.hidden=!!job||applied.orientation===o;note.textContent=note.hidden?'':(own?T.ownShape:T.madeFor)[applied.orientation];}
      }
    }
    // Notes and errors for their own design, under the art row in every place (shown, not spoken: say() speaks). ask:
    // the line asks for the design ("Artwork only" with none), until a design or another front makes it old news.
    // "Continue without it" after their design failed to save: Buy goes on with the card as it shows.
    function without(shown){for(const button of doc.querySelectorAll('[data-pfai-own-without]'))button.hidden=!shown;}
    for(const button of doc.querySelectorAll('[data-pfai-own-without]'))button.addEventListener('click',()=>{win.PocketAssets?.dismiss?.('background_art');without(false);ownStatus('');say(T.ownWithout);});
    function ownStatus(message,{error=false,ask=false}={}){
      asking=ask&&!!message;
      for(const place of places()){const node=place.querySelector('[data-pfai-own-status]');if(!node)continue;node.textContent=message||'';node.classList.toggle('is-error',!!error&&!!message);}
    }
    // Focus after a change of art: the row's first action in the place they used, else what they used (never the page).
    function focusRow(place,from){
      const row=(place||places().find(shown))?.querySelector('[data-pfai-on]');
      const target=row&&[row.querySelector('[data-pfai-top]'),...row.querySelectorAll('[data-pfai-change],[data-pfai-own-replace],[data-pfai-remove]')].find(shown);
      (target||(shown(from)?from:null))?.focus?.({preventScroll:true});
    }

    /* ---------- Saving a file (their own finished design) ---------- */
    // A file made ready in the browser, saved as their artwork (the upload's type below).
    async function upload(ready,{failed=T.ownFailed,name='design'}={}){
      const body=new win.FormData();body.append('file',ready,ready.name||name);body.append('type','custom_artwork');body.append('session',core().sessionId());
      const controller=new win.AbortController(),timer=win.setTimeout(()=>controller.abort(),UPLOAD_MS);
      let response;try{response=await win.fetch(base()+'/v1/assets/upload',{method:'POST',credentials:'omit',headers:{Accept:'application/json'},body,signal:controller.signal});}
      catch(_){throw Error(controller.signal.aborted?T.timeout+' Try again in a moment.':T.network+' Check your connection and try again.');}finally{win.clearTimeout(timer);}
      let data=null;try{data=await response.json();}catch(_){}
      const COPY=win.PocketAssetService?.COPY||{};
      if(!response.ok){
        const code=data?.code;
        throw Error(code==='too_small'?T.tooSmall:['too_large','unsupported_type','decode_failed','rate_limited'].includes(code)&&COPY[code]?COPY[code]:response.status===413&&COPY.too_large?COPY.too_large:response.status===429&&COPY.busy?COPY.busy:failed);
      }
      try{return win.PocketAssetService.validateAsset(data);}catch(_){throw Error(failed);}
    }

    /* ---------- Their own finished design ---------- */
    // pf_art_upload_* as "Upload your logo" sends them (never a file name or an address), path upload_design.
    const track=(name,props)=>{try{win.PocketAnalytics?.capture?.(name,{path:'upload_design',...props});}catch(_){}};
    const mimeOf=chosen=>{const t=String(chosen?.type||'').toLowerCase(),n=String(chosen?.name||'').toLowerCase();return /^image\/(png|jpeg|webp|gif|svg\+xml|heic|heif)$/.test(t)||t==='application/pdf'?t:/\.hei[cf]$/.test(n)?'image/heic':/\.pdf$/.test(n)?'application/pdf':'other';};
    // The card turned to a shape with its own control (so everything that follows the card follows); false if it was not.
    function turn(shape){
      const radio=shape!==orientation()&&doc.querySelector(`[data-card-field="orientation"][value="${shape}"]`);if(!radio)return false;
      radio.checked=true;radio.dispatchEvent(new win.Event('change',{bubbles:true}));return true;
    }
    // What their design does to the card, from its size: turned to match it, trimmed edges, a soft print.
    function ownNotes(w,h,turned){
      const notes=turned?[T.ownTurned[h>w?'Vertical':'Horizontal']]:[];
      if(w&&h&&Math.abs(Math.max(w,h)/Math.min(w,h)-CARD_RATIO)/CARD_RATIO>.06)notes.push(T.ownTrimmed);
      if(w&&h&&Math.min(w,h)<SHARP_SHORT)notes.push(T.ownSoft);
      return notes;
    }
    // Made ready like any upload (HEIC and PDF converted, big photos made smaller), saved, then put on the card as it is:
    // full-bleed, their logo and name off. A card that does not exist yet is made first (pocket-reviews-v2.js), so a
    // design alone is enough to order. The card turns to the design's shape; trimmed edges and a soft file are said.
    // Its row is there from the moment the file is picked, and the card turns as soon as the file is read, so the page
    // does not move when the upload ends. Buy waits until it is saved (pocket:card-config, state().saving).
    // back: where focus was when they asked for the file (pickOwn). Chosen outside the art rows ("Artwork only" in the
    // design options), that control keeps its place on screen while the rows and the card above it change, so it never
    // slides under the pinned card; a touch, a click, the wheel or a key of theirs ends the hold.
    async function takeOwn(chosen,from,back=null){
      if(!chosen||ownJob)return;
      const wasArtOnly=!!cards()?.artOnly?.(),was=orientation(),facts={mime:mimeOf(chosen),bytes:chosen.size};
      const keep=back&&back!==doc.body&&!placeOf(back)&&shown(back)?back.closest('label')||back:null,top0=keep?.getBoundingClientRect().top,stay=new win.AbortController();
      if(keep)for(const type of ['wheel','touchstart','pointerdown','keydown'])win.addEventListener(type,()=>stay.abort(),{capture:true,passive:true,signal:stay.signal});
      const hold=()=>{if(!keep||stay.signal.aborted)return;const r=keep.getBoundingClientRect();if(!r.height)return;const d=r.top-top0;if(Math.abs(d)>2)win.scrollBy({top:d,behavior:'instant'});};
      const job={preview:picture(chosen),shape:'',turned:false,was,wasArtOnly,stay};ownJob=job;ownStatus('');without(false);renderPlaces();say(T.ownSaving);
      // In the order's record of uploads ("background_art"): Buy waits for it, and it is never dropped by a layout change.
      const record=win.PocketAssets?.track?.('custom_artwork',chosen,'background_art');
      doc.dispatchEvent(new win.CustomEvent('pocket:card-config'));hold();
      track('pf_art_upload_started',facts);
      try{
        const service=win.PocketAssetService,ready=service?.prepareFile?await service.prepareFile(chosen):{file:chosen};
        if(ownJob!==job)return;
        const type=String(ready.file.type||'').toLowerCase();if(type&&!IMAGE_TYPES.includes(type))throw Error(T.ownType);
        // A HEIC or PDF has a picture once it is made ready.
        if(!job.preview)job.preview=picture(ready.file);
        // Their card, showing where the design goes ("Your design") while it uploads.
        doc.dispatchEvent(new win.CustomEvent('pocket:ensure-brand'));
        cards()?.setFront?.('Artwork Only',{byShopper:true});
        // Its shape is known: the card turns now and the notes take their place.
        const rw=Number(ready.width)||0,rh=Number(ready.height)||0;
        if(rw&&rh){job.shape=rh>rw?'Vertical':'Horizontal';job.turned=turn(job.shape);ownStatus(ownNotes(rw,rh,job.turned).join(' '));}
        renderPlaces();hold();
        const asset=await upload(ready.file,{failed:T.ownFailed,name:'design'});
        await record?.done(asset,ready.file);
        if(ownJob!==job)return;
        ownJob=null;
        const w=Number(asset.width)||0,h=Number(asset.height)||0,shape=h>w?'Vertical':'Horizontal';
        turn(shape);
        const notes=[T.ownApplied,...ownNotes(w,h,shape!==was&&orientation()===shape)];
        track('pf_art_upload_succeeded',{...facts,w,h});
        apply({assetId:asset.assetId,orientation:shape,width:w,height:h,own:true},{source:'upload',announce:false});
        drop(job.preview);
        cards()?.setFront?.('Artwork Only',{byShopper:true});
        ownStatus(notes.slice(1).join(' '));say(notes.join(' '));
        // Chosen outside the art rows ("Artwork only" in the design options): focus stays there (or comes back, if it
        // was lost). From the row's own buttons: the row.
        const at=doc.activeElement;
        if(back&&back!==doc.body&&!placeOf(back)&&shown(back)){if(!at||at===doc.body||!shown(at))back.focus({preventScroll:true});}
        else focusRow(placeOf(from));
        saveCard();
      }catch(error){
        // Not made into a picture here: kept as it is where the Worker can keep it (checked by hand, nothing held up).
        // Anything else holds Buy until it is tried again, replaced, or let go ("Continue without it").
        const kept=error?.code&&record?await record.keep(error).catch(()=>null):null;
        if(!error?.code)record?.fail(error);
        if(ownJob!==job)return;
        if(!kept)without(true);
        ownJob=null;drop(job.preview);
        // Nothing went on the card: its shape and their logo or name stay (or come back) as they were.
        if(job.turned&&orientation()===job.shape)turn(was);
        if(!wasArtOnly)identityBack();
        track('pf_art_upload_failed',{...facts,code:'unknown'});
        const message=error?.message&&!/^(load|decode|encode)$/i.test(error.message)?error.message:T.ownFailed;
        ownStatus(message,{error:true});say(message);
      }finally{
        for(const input of doc.querySelectorAll('[data-pfai-own-file]'))input.value='';
        renderPlaces();
        if(!ownJob)doc.dispatchEvent(new win.CustomEvent('pocket:card-config'));
        hold();stay.abort();
      }
    }
    // "Upload it for the whole front" and "Replace" open the file picker of the place they were pressed in; back is
    // where focus is meanwhile (the design options' "Artwork only" asks through step 01's button).
    let ownFrom=null,ownBack=null;
    function pickOwn(button,back=doc.activeElement){ownFrom=button;ownBack=back;(placeOf(button)?.querySelector('[data-pfai-own-file]')||doc.querySelector('[data-pfai-own-file]'))?.click();}

    /* ---------- The art step: their words in, a design back, in the background ---------- */
    /**
     * Makes art for the whole front from their words (pocket-design.js reads them from the hero's one box). Nothing is
     * shown or waited on here: the caller says what is happening. One design at a time: asked again while one is being
     * made, the same answer comes back. Resolves to {ok: true, design, remaining} (the design is kept for this visit,
     * not put on the card: apply() does that), or {ok: false, error, code}: the Worker's own sentence when it gave one.
     * @param {string} words - the look, as the visitor described it
     * @param {{orientation?: 'Horizontal'|'Vertical'}} [options]
     */
    function make(words,{orientation:shape=orientation()}={}){
      if(making)return making;
      making=(async()=>{
        const on=await ready;
        if(!on)return {ok:false,code:'off',error:T.off};
        if(remaining===0||resting)return {ok:false,code:'ai_art_resting',error:restingText||'You’ve made 3 designs today.'};
        const description=clean(words);
        if(chars(description)<MIN_TEXT)return {ok:false,code:'description_invalid',error:T.tooShort};
        const sessionId=core().sessionId(),asked={sessionId,orientation:shape==='Vertical'?'Vertical':'Horizontal',description:[...description].slice(0,MAX_TEXT).join('')};
        const controller=new win.AbortController();
        let timedOut=false,response=null,data=null;
        const timer=win.setTimeout(()=>{timedOut=true;controller.abort();},options.timeoutMs||GENERATE_MS);
        try{
          response=await win.fetch(base()+'/v1/art/generate',{method:'POST',credentials:'omit',headers:{'Content-Type':'application/json',Accept:'application/json','X-PF-Session':sessionId},body:JSON.stringify(asked),signal:controller.signal});
          try{data=await response.json();}catch(_){data=null;}
        }catch(_){response=null;}finally{win.clearTimeout(timer);}
        if(!response)return {ok:false,code:timedOut?'timeout':'network',error:timedOut?T.timeout:T.network};
        if(response.ok){
          const made=design({...data?.artwork,typed:true,fromMockup:false,description:typeof data?.artwork?.description==='string'&&clean(data.artwork.description)?data.artwork.description:asked.description});
          if(Number.isInteger(data?.remaining)&&data.remaining>=0)remaining=Math.min(MAX,data.remaining);
          if(!made){persist();return {ok:false,code:'unusable',error:T.unusable};}
          // Numbered after the last one made: the newest has the highest number, and no design is renumbered.
          made.n=Math.max(0,...designs.map(d=>d.n||0))+1;
          designs=[made,...designs.filter(d=>d.assetId!==made.assetId)].slice(0,MAX);
          persist();render();
          return {ok:true,design:{...made},remaining};
        }
        // The Worker's own sentence, as it is (as text).
        const message=typeof data?.error==='string'&&data.error.trim()?data.error.trim().slice(0,400):T.failed;
        if(response.status===404){enabled=false;render();return {ok:false,code:'off',error:T.off};}
        // Over a cap or the day's AI budget: no more designs today (a reload remembers it).
        if(response.status===429&&data?.code==='ai_art_resting'){resting=String(data.reason||'resting').slice(0,40);restingText=message;if(data.reason==='session_limit')remaining=0;persist();}
        return {ok:false,code:String(data?.code||response.status),error:message};
      })().finally(()=>{making=null;});
      return making;
    }

    /* ---------- Events ---------- */
    doc.addEventListener('click',event=>{
      const target=event.target.closest?.('[data-pfai-change],[data-pfai-remove],[data-pfai-own],[data-pfai-own-replace]');
      if(!target)return;
      const d=target.dataset;
      if('pfaiOwn' in d||'pfaiOwnReplace' in d){pickOwn(target);return;}
      // "Change": the one box their words are in.
      if('pfaiChange' in d){doc.dispatchEvent(new win.CustomEvent('pocket:design-words'));return;}
      if('pfaiRemove' in d){
        const place=placeOf(target);remove();saveCard();
        [place?.querySelector('[data-pfai-own]')].find(shown)?.focus({preventScroll:true});
      }
    });
    // Their own design, chosen in any place; "Show my logo or name on it" over any art (the front's "Artwork Only" off or
    // on).
    doc.addEventListener('change',event=>{
      const input=event.target;
      if(input?.matches?.('[data-pfai-own-file]')){const chosen=input.files?.[0];if(chosen)takeOwn(chosen,ownFrom||input,ownBack);return;}
      if(!input?.matches?.('[data-pfai-top]')||!applied)return;
      const renderer=cards();if(!renderer?.setFront)return;
      if(input.checked)renderer.frontWithIdentity({byShopper:true});else renderer.setFront('Artwork Only',{byShopper:true});
      say(input.checked?T.topOn:T.topOff);saveCard();
    });
    // An upload still on its way when the design changes: it goes on no card, and the card is put back as it was.
    function cancelOwn(){
      const job=ownJob;if(!job)return;
      ownJob=null;job.stay.abort();drop(job.preview);ownStatus('');
      if(job.turned&&orientation()===job.shape)turn(job.was);
      if(!job.wasArtOnly)identityBack();
    }
    // A new design (assets/pocket.js: "Add another design", or another business) starts without the last one's art.
    // Their designs stay in the list, to use again. A design that was their art alone keeps "Artwork only" and asks for
    // this card's design (nothing else of theirs to show).
    doc.addEventListener('pocket:design-reset',()=>{cancelOwn();dismissed='';if(applied)remove({announce:false,dismiss:false});else{persist();if(!identityBack())ownStatus(T.ownAsk,{ask:true});}render();});
    // A saved card opened from its link (?card=, pocket-card-profile.js) brings its own art: an AI design or their own.
    // A saved card without art has none, whatever this tab showed before; except the card this tab put its art on,
    // read back before that save landed (a quick reload): the art stays and the card is saved with it.
    function takeProfileArt(art,slug){
      if(!art){if(applied&&artCard&&slug===artCard)saveCard();else if(applied)remove({announce:false,dismiss:false});return;}
      const own=art?.source==='customer-upload';
      if(typeof art!=='object'||!ASSET.test(String(art.assetId||''))||art.source&&art.source!=='ai-generated'&&!own||art.assetId===dismissed)return;
      const words=!own&&typeof art.description==='string'?clean(art.description):'';
      apply({assetId:art.assetId,orientation:art.orientation,description:words,typed:!!words&&words!==FROM_MOCKUP,fromMockup:words===FROM_MOCKUP,own},{source:'profile',announce:false,card:SLUG.test(slug||'')?slug:cardSlug()});
    }
    doc.addEventListener('pocket:card-profile-art',event=>takeProfileArt(event.detail?.backgroundArt,event.detail?.slug));
    // The card turned (or changed): the row says when the design was made for the other orientation, and the line says
    // whether their logo or name is drawn over the art (the front can change in the design options too). Another front
    // than "Artwork only" needs no design: the ask for one goes.
    doc.addEventListener('pocket:card-config',()=>{if(applied)write(properties(applied,{top:top()}));if(applied||ownJob)renderPlaces();if(asking&&!cards()?.artOnly?.())ownStatus('');});
    // A name or a logo arriving (or going) shows or hides "Show my logo or name on it".
    win.PocketPersonalization?.store?.subscribe?.(()=>{if(applied||ownJob)renderPlaces();});
    // The theme editor re-renders the product section: its new copy of the panel is dropped (this one stays in charge).
    doc.addEventListener('shopify:section:load',()=>{for(const node of doc.querySelectorAll('[data-pfai-live]'))if(node!==live)node.remove();render();});

    const another=restore();
    if(applied){const kept=applied;applied=null;apply(kept,{source:kept.source,announce:false,card:artCard});}
    // A saved card that opened before this script started: its art (or none) is the card's.
    const page=win.PocketCardProfile?.page?.state;if(page?.opened||page?.backgroundArt)takeProfileArt(page.backgroundArt||null,page.slug);
    // No art on this design: a new design begun before this script started (/?new_design=1) gets their logo or name
    // back, as on pocket:design-reset (a saved card already open keeps its own front). "Artwork only" with no design
    // asks for one.
    if(!applied){if(another&&!page?.opened)identityBack();if(cards()?.artOnly?.())ownStatus(T.ownAsk,{ask:true});}
    render();
    // Put on or taken off from outside the row (the one box's pipeline and the Art choice, pocket-design.js): the saved
    // card follows, as it does for the row's own Remove and "Show my logo or name on it".
    const put=(d,options)=>{const done=apply(d,options);if(done)saveCard();return done;};
    const takeOff=options=>{const had=!!applied;remove(options);if(had)saveCard();};
    return {ready,make,apply:put,remove:takeOff,takeOwn,state:()=>({enabled,designs:designs.map(d=>({...d})),remaining,resting,applied:applied?{...applied}:null,busy:!!making,saving:!!ownJob})};
  }
  return {init,clean,design,properties,artDescription,FROM_MOCKUP};
});
